import { Container, Sprite } from 'pixi.js';
import { colOf, inBounds } from '../sim/constants';
import type { PeepJob, SimState } from '../sim/types';
import type { GrayboxTextures } from './graybox';
import { ELEV_STEP, HALF_H, HALF_W } from './iso';

const JOB_COLORS: Record<PeepJob, number> = {
  miner: 0xd07f2a,
  hauler: 0x8a9a4e,
  staff: 0x5f87c9,
  industry: 0x4e9a8a,
  idle: 0x9a9a9a,
};

/** Tier badge = hat color: transient brown, regular steel, veteran gold, settler red. */
const TIER_HATS = [0x8a6a3a, 0xb8b2a8, 0xe8c170, 0xd06a4e] as const;
const SPOUSE_BODY = 0x9a6ab8;
const CHILD_BODY = 0xd9a45a;

function depthAtTile(state: SimState, x: number, y: number): number {
  const tx = Math.floor(x);
  const ty = Math.floor(y);
  if (!inBounds(tx, ty)) return 0;
  return state.terrain.depth[colOf(tx, ty)] ?? 0;
}

/** Pooled peep sprites, interpolated between sim steps. */
export class PeepLayer {
  readonly container = new Container();
  private readonly tex: GrayboxTextures;
  private readonly pool: Sprite[] = [];
  /** Mini-cows trailing haulers who carry cattle. */
  private readonly cowPool: Sprite[] = [];

  constructor(tex: GrayboxTextures) {
    this.tex = tex;
    this.container.sortableChildren = true;
  }

  update(state: SimState, alpha: number): void {
    const peeps = state.peeps;
    while (this.pool.length < peeps.length) {
      const sprite = new Sprite();
      sprite.anchor.set(0.5, 1);
      sprite.cullable = true;
      this.pool.push(sprite);
      this.container.addChild(sprite);
    }
    // Gait clock runs on sim time, so fast-forward means faster legs.
    const simSeconds = (state.meta.tick + alpha) * 0.1;
    const gait = (Math.floor(simSeconds * 5) % 2 === 0 ? 1 : 2) as 1 | 2;
    let cowsUsed = 0;
    for (let index = 0; index < this.pool.length; index += 1) {
      const sprite = this.pool[index]!;
      const peep = peeps[index];
      if (!peep) {
        sprite.visible = false;
        continue;
      }
      sprite.visible = true;
      const moving = Math.abs(peep.x - peep.px) + Math.abs(peep.y - peep.py) > 1e-4;
      const frame = moving ? gait : 0;
      if (peep.role === 'child') {
        sprite.texture = this.tex.kid(CHILD_BODY, frame);
      } else if (peep.role === 'spouse') {
        sprite.texture = this.tex.peep(SPOUSE_BODY, frame, 0xece2c8);
      } else {
        sprite.texture = this.tex.peep(JOB_COLORS[peep.job], frame, TIER_HATS[peep.tier] ?? TIER_HATS[0]);
      }
      const fx = peep.px + (peep.x - peep.px) * alpha;
      const fy = peep.py + (peep.y - peep.py) * alpha;
      const prevDepth = depthAtTile(state, peep.px, peep.py);
      const currDepth = depthAtTile(state, peep.x, peep.y);
      const depth = prevDepth + (currDepth - prevDepth) * alpha;
      // Tile-center continuous coords -> screen; feet at the floor.
      sprite.position.set((fx - fy) * HALF_W, (fx + fy) * HALF_H + depth * ELEV_STEP);
      sprite.zIndex = (fx + fy) * 16;

      // Cattle cargo walks itself: a cow on a lead, lagging half a tile.
      if (peep.carryGood === 'cattle' && peep.carryDt > 0) {
        let cow = this.cowPool[cowsUsed];
        if (!cow) {
          cow = new Sprite();
          cow.anchor.set(0.5, 1);
          cow.cullable = true;
          this.cowPool.push(cow);
          this.container.addChild(cow);
        }
        cowsUsed += 1;
        cow.visible = true;
        cow.texture = this.tex.cow(moving ? ((gait - 1) as 0 | 1) : 0);
        let cx = fx - 0.3;
        let cy = fy + 0.15;
        if (moving) {
          const dx = peep.x - peep.px;
          const dy = peep.y - peep.py;
          const len = Math.hypot(dx, dy);
          if (len > 1e-6) {
            cx = fx - (dx / len) * 0.4;
            cy = fy - (dy / len) * 0.4;
          }
        }
        cow.position.set((cx - cy) * HALF_W, (cx + cy) * HALF_H + depth * ELEV_STEP);
        cow.zIndex = (cx + cy) * 16 - 1;
      }
    }
    for (let index = cowsUsed; index < this.cowPool.length; index += 1) {
      this.cowPool[index]!.visible = false;
    }
  }
}
